import { hasPermission } from '@/utils/permission'

/** 派工编辑权（桌面派工管理、合并数量等） */
export function canEditDispatch(user) {
  return hasPermission(user, 'orders.dispatch.edit')
}

function isAdminLike(user) {
  return !!(user && (user.is_admin || user.is_superuser))
}

function canBusinessOverridePackaging(user) {
  if (!user) return false
  if (isAdminLike(user)) return true
  return hasPermission(user, 'orders.dispatch.edit') || hasPermission(user, 'orders.dispatch.review')
}

function isPackagingOperator(user, orderOrRow) {
  if (!user || !orderOrRow) return false
  const op = orderOrRow.packaging_operator_user_id
  if (op == null || op === '') return false
  return Number(op) === Number(user.user_id)
}

/**
 * 现场可操作派工单（跑码/分码）：与后端 _can_operate_assigned_dispatch 对齐。
 */
export function canOperateAssignedDispatch(user, orderOrRow) {
  if (!user || !orderOrRow) return false
  if (isAdminLike(user)) return true
  if (
    hasPermission(user, 'orders.dispatch.view') ||
    hasPermission(user, 'orders.dispatch.edit') ||
    hasPermission(user, 'orders.dispatch.review') ||
    hasPermission(user, 'operations.execute')
  ) {
    return true
  }
  const aid = orderOrRow.assigned_user_id
  if (aid == null || aid === '') return false
  return Number(aid) === Number(user.user_id)
}

/**
 * 包装称重保存：未锁定时任意登录用户可写；保存后锁定给操作人；业务/管理员始终可写。
 */
export function canOperateDispatchPackaging(user, orderOrRow) {
  if (!user || !orderOrRow) return false
  if (canBusinessOverridePackaging(user)) return true
  const op = orderOrRow.packaging_operator_user_id
  if (op != null && op !== '') {
    return isPackagingOperator(user, orderOrRow)
  }
  return true
}

/** 查看包装页：已审派工单任意登录用户可进入 */
export function canViewDispatchPackaging(user, orderOrRow) {
  if (!user || !orderOrRow) return false
  if (orderOrRow.status === 'cancelled') return false
  return true
}

/** 进入派工详情 / 跑码 */
export function canOpenDispatchDetail(user) {
  return (
    hasPermission(user, 'orders.dispatch.view') ||
    hasPermission(user, 'orders.dispatch.edit') ||
    hasPermission(user, 'orders.dispatch.review') ||
    hasPermission(user, 'operations.execute')
  )
}

/** @deprecated 已取消申请流程，保留 API 兼容 */
export function canRequestPackagingAccess() {
  return false
}

export function packagingOperatorDisplay(order) {
  if (!order) return ''
  return order.packaging_operator_user_name || ''
}

export function packLineMeasuresIncomplete(row) {
  if (!row) return false
  if (row.needWeigh && !(row.weightStr != null && String(row.weightStr).trim() !== '')) return true
  if (row.needVolume) {
    const dims = [row.lenStr, row.widStr, row.hgtStr].every(
      (x) => x != null && String(x).trim() !== ''
    )
    if (!dims) return true
  }
  return false
}

export function packLinesHaveIncompleteMeasures(packLines, { branchRecordId = null } = {}) {
  let lines = Array.isArray(packLines) ? packLines : []
  if (branchRecordId != null) {
    const bid = Number(branchRecordId)
    lines = lines.filter((row) => Number(row.branch_record_id) === bid)
  }
  return lines.some(packLineMeasuresIncomplete)
}

/** 包装称重是否只读：已取消、或包装资料已提交/已通过业务核销。打码完成不影响填写。 */
export function isDispatchPackagingReadonly(order, packLines = null, options = {}) {
  if (!order) return true
  if (order.status === 'cancelled') return true
  const pa = (order.packaging_audit_status || 'na').toLowerCase()
  if (pa === 'pending') return true
  if (pa === 'approved') return true
  const fromTask = !!options.fromPackagingTask
  const orderIncomplete = !!options.packagingMeasuresIncomplete
  if (fromTask || orderIncomplete) return false
  const lines = Array.isArray(packLines) ? packLines : []
  const bid = options.taskBranchId != null ? Number(options.taskBranchId) : null
  if (packLinesHaveIncompleteMeasures(lines, { branchRecordId: bid })) return false
  return false
}

export function canEditDispatchPackagingMeasures(user, order, packLines = null, options = {}) {
  if (!canOperateDispatchPackaging(user, order)) return false
  if (!order || order.status === 'cancelled') return false
  const fromTask = !!options.fromPackagingTask
  const orderIncomplete = !!options.packagingMeasuresIncomplete
  if (fromTask || orderIncomplete) return true
  if (isDispatchPackagingReadonly(order, packLines, options)) return false
  return true
}

export function canSubmitPackagingAudit(order, packLines = null) {
  if (!order || order.status === 'cancelled') return false
  const pa = (order.packaging_audit_status || 'na').toLowerCase()
  if (pa === 'pending' || pa === 'approved') return false
  const lines = Array.isArray(packLines) ? packLines : []
  if (packLinesHaveIncompleteMeasures(lines)) return false
  return true
}

export function packagingAuditStatusLabel(status) {
  const map = {
    na: '未提交',
    pending: '待包装核销',
    approved: '包装已核销',
    rejected: '包装核销退回'
  }
  return map[(status || 'na').toLowerCase()] || status || '—'
}
